/**
 * Card de campanha de brinde (JON-186).
 *
 * Selo pequeno e discreto no canto inferior esquerdo, com ícone de brinde,
 * mostrando quanto falta para o cliente ganhar o brinde. NÃO é um pop-up: é
 * compacto, fechável e não ocupa a tela. Respeita o agendamento (JON-185):
 * fora da vigência o gate do global-script zera giftSystemConfig.enabled e o
 * card se esconde sozinho.
 *
 * FONTE DO PROGRESSO
 * Este card espelha a MESMA regra da barra de progresso (gift-progress-bar.js),
 * para nunca mostrar um número diferente do que o cliente vê na barra:
 *
 *   - percentual e copy vêm de window.computeGiftProgressPercent e
 *     window.resolveGiftProgressMessage, que a própria barra publica;
 *   - a seleção de nível, o total elegível e a checagem de produto gatilho são
 *     replicados aqui porque na barra são métodos de instância do custom
 *     element, inacessíveis de fora.
 *
 * Essa replicação é travada por tests/gift-badge-parity.test.js, que roda o
 * card e a barra sobre os mesmos carrinhos e falha se divergirem. Ao mexer na
 * regra da barra, rode aquele teste.
 */
(function () {
  'use strict';

  var DISMISS_KEY = 'gift_campaign_badge_dismissed_v1';
  var STYLE_ID = 'gift-campaign-badge-styles';

  function campaignSignature(levels) {
    if (!Array.isArray(levels)) {
      return '';
    }

    return levels
      .map(function (level) {
        var gifts = Array.isArray(level && level.availableGifts)
          ? level.availableGifts
            .map(function (gift) {
              return gift && typeof gift === 'object'
                ? (gift.handle || gift.id || '')
                : gift;
            })
            .join('~')
          : '';

        return [level && level.levelNumber, level && level.minValue, gifts].join(':');
      })
      .join('|');
  }

  function formatBRL(value) {
    var number = Number(value);

    if (!isFinite(number)) {
      number = 0;
    }

    return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  // --- Espelho da regra da barra (gift-progress-bar.js) -------------------

  // Igual a getEligibleCartTotalReais: original_line_price em reais, ignorando
  // as linhas de brinde.
  function eligibleCartTotalReais(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }

    var cents = cart.items.reduce(function (total, item) {
      if (item.properties && item.properties._is_gift === 'true') {
        return total;
      }
      return total + (item.original_line_price || item.line_price || 0);
    }, 0);

    return cents / 100;
  }

  // Igual a resolveTriggerProgress da barra: quantidade e modo de contagem
  // vêm do núcleo único (assets/gift-choice-core.js). Sem cópia local da
  // regra: motor, barra e card chamam a mesma função. Devolve o objeto
  // completo (required/matched/missing/satisfied) para alimentar tanto a
  // elegibilidade quanto o preenchimento proporcional e a mensagem de
  // quantidade faltante.
  function resolveTriggerProgress(level, cart) {
    var core = typeof window !== 'undefined' && window.GiftChoiceCore;

    if (!core || typeof core.resolveTriggerProgress !== 'function') {
      // Núcleo indisponível: sem gatilho configurado considera satisfeito,
      // como sempre; com gatilho configurado, falha fechado.
      var hasTriggerProducts = Boolean(level && level.triggerProducts && level.triggerProducts.length);
      return {
        required: hasTriggerProducts ? 1 : 0,
        matched: 0,
        missing: hasTriggerProducts ? 1 : 0,
        satisfied: !hasTriggerProducts,
      };
    }

    return core.resolveTriggerProgress({
      cartItems: cart && cart.items,
      triggerProducts: level && level.triggerProducts,
      requiredCount: level && level.triggerMinCount,
      countMode: level && level.triggerCountMode,
    });
  }

  function hasTriggerProductInCart(level, cart) {
    return resolveTriggerProgress(level, cart).satisfied;
  }

  // Igual a resolveTargetLevel da barra: nível atual/próximo/alvo vêm do
  // núcleo único. Sem cópia local do critério de ordenação/seleção.
  function resolveTargetLevel(levels, cartTotal) {
    var core = typeof window !== 'undefined' && window.GiftChoiceCore;

    if (!core || typeof core.resolveTargetLevel !== 'function') {
      return { currentLevel: null, nextLevel: null, targetLevel: null };
    }

    return core.resolveTargetLevel({ levels: levels, cartTotal: cartTotal });
  }

  function checkLevelEligibility(level, cart) {
    if (!cart) {
      return false;
    }

    if (eligibleCartTotalReais(cart) < level.minValue) {
      return false;
    }

    return hasTriggerProductInCart(level, cart);
  }

  /**
   * Reproduz a decisão de nível da barra e devolve o que o card precisa.
   * Estados: 'progress' (falta valor), 'trigger-missing' (valor ok, falta o
   * produto gatilho) e 'unlocked'.
   */
  function resolveBadgeView(config, cart) {
    var hidden = {
      visible: false,
      state: 'hidden',
      percent: 0,
      remaining: 0,
      missing: 0,
      levelNumber: null,
    };

    if (!config || !Array.isArray(config.levels) || !cart || !Array.isArray(cart.items)) {
      return hidden;
    }

    var computePercent = typeof window !== 'undefined' && window.computeGiftProgressPercent;

    if (typeof computePercent !== 'function') {
      return hidden;
    }

    var cartTotal = eligibleCartTotalReais(cart);
    var target = resolveTargetLevel(config.levels, cartTotal);
    var nextLevel = target.nextLevel;
    var currentLevel = target.currentLevel;

    if (!nextLevel && !currentLevel) {
      return hidden;
    }

    if (currentLevel && !nextLevel) {
      var needsTrigger = Boolean(
        currentLevel.triggerProducts && currentLevel.triggerProducts.length > 0,
      );
      var triggerProgress = resolveTriggerProgress(currentLevel, cart);

      if (checkLevelEligibility(currentLevel, cart)) {
        return {
          visible: true,
          state: 'unlocked',
          percent: 100,
          remaining: 0,
          missing: 0,
          levelNumber: currentLevel.levelNumber,
        };
      }

      return {
        visible: true,
        state: 'trigger-missing',
        percent: computePercent(cartTotal, currentLevel.minValue, {
          needsTrigger: needsTrigger,
          triggerProgress: triggerProgress,
        }),
        remaining: 0,
        missing: triggerProgress.missing,
        levelNumber: currentLevel.levelNumber,
      };
    }

    var nextNeedsTrigger = Boolean(
      nextLevel.triggerProducts && nextLevel.triggerProducts.length > 0,
    );
    var nextTriggerProgress = resolveTriggerProgress(nextLevel, cart);

    return {
      visible: true,
      state: 'progress',
      percent: computePercent(cartTotal, nextLevel.minValue, {
        needsTrigger: nextNeedsTrigger,
        triggerProgress: nextTriggerProgress,
      }),
      remaining: Math.max(nextLevel.minValue - cartTotal, 0),
      missing: nextTriggerProgress.missing,
      levelNumber: nextLevel.levelNumber,
    };
  }

  /**
   * Copy do card. Nos estados 'unlocked' e 'progress' usa os textos próprios
   * do card (settings gift_campaign_badge_*). Em 'trigger-missing' delega para
   * a mesma copy da barra, que é quem sabe falar de produto em vez de valor.
   */
  function resolveBadgeMessage(view, config, badge) {
    if (view.state === 'unlocked') {
      return badge.textReached || 'Seu brinde está garantido!';
    }

    if (view.state === 'trigger-missing') {
      var resolveMessage = typeof window !== 'undefined' && window.resolveGiftProgressMessage;

      if (typeof resolveMessage === 'function') {
        var message = resolveMessage('trigger_missing', config, {
          remaining: 0,
          missing: view.missing,
          levelNumber: view.levelNumber,
          formatCurrency: formatBRL,
        });

        if (message) {
          return message;
        }
      }

      return badge.textReached || 'Seu brinde está garantido!';
    }

    var template = badge.text || 'Falta {valor} para ganhar seu brinde';

    return template.replace('{valor}', formatBRL(view.remaining));
  }

  // Exposto para o teste de paridade e para depuração no console.
  if (typeof window !== 'undefined') {
    window.GiftCampaignBadgeCore = {
      resolveBadgeView: resolveBadgeView,
      resolveBadgeMessage: resolveBadgeMessage,
      eligibleCartTotalReais: eligibleCartTotalReais,
      resolveTriggerProgress: resolveTriggerProgress,
      resolveTargetLevel: resolveTargetLevel,
      hasTriggerProductInCart: hasTriggerProductInCart,
      checkLevelEligibility: checkLevelEligibility,
    };
  }

  var GiftCampaignBadge = (
    typeof HTMLElement !== 'undefined'
      ? class extends HTMLElement {
        connectedCallback() {
          if (this._initialized) {
            return;
          }

          this._initialized = true;
          this._currentSignature = '';
          this.injectStyles();
          this.buildDom();

          this._onCartUpdate = this.onCartUpdate.bind(this);
          document.addEventListener('cart-update', this._onCartUpdate);

          if (document.readyState === 'complete') {
            this.refresh();
          } else {
            window.addEventListener('load', this.refresh.bind(this));
          }
        }

        disconnectedCallback() {
          if (this._onCartUpdate) {
            document.removeEventListener('cart-update', this._onCartUpdate);
          }
        }

        injectStyles() {
          if (document.getElementById(STYLE_ID)) {
            return;
          }

          var style = document.createElement('style');
          style.id = STYLE_ID;
          style.textContent = [
            '.gift-campaign-badge__pill{',
            'position:fixed;left:16px;bottom:16px;z-index:40;',
            'display:none;align-items:center;gap:9px;max-width:280px;',
            'background:#fff;color:#232323;border:1px solid #e6e6e6;',
            'box-shadow:0 6px 20px rgba(0,0,0,.14);border-radius:999px;',
            'padding:9px 10px 9px 13px;',
            'font-family:inherit;font-size:13px;line-height:1.35;',
            'animation:giftCampaignBadgeIn .28s ease both;',
            '}',
            '.gift-campaign-badge__pill[data-visible="true"]{display:flex;}',
            '.gift-campaign-badge__icon{font-size:18px;line-height:1;flex:none;}',
            '.gift-campaign-badge__text{flex:1 1 auto;}',
            '.gift-campaign-badge__close{flex:none;background:none;border:none;',
            'cursor:pointer;font-size:15px;line-height:1;color:#9a9a9a;',
            'padding:2px 4px;margin:0;border-radius:50%;}',
            '.gift-campaign-badge__close:hover{color:#232323;}',
            '@keyframes giftCampaignBadgeIn{from{opacity:0;transform:translateY(8px);}',
            'to{opacity:1;transform:translateY(0);}}',
            '@media (max-width:600px){.gift-campaign-badge__pill{left:12px;bottom:12px;',
            'max-width:calc(100vw - 24px);font-size:12.5px;}}',
            '@media (prefers-reduced-motion:reduce){.gift-campaign-badge__pill{animation:none;}}',
          ].join('');
          document.head.appendChild(style);
        }

        buildDom() {
          var pill = document.createElement('div');
          pill.className = 'gift-campaign-badge__pill';
          pill.setAttribute('role', 'status');
          pill.setAttribute('aria-live', 'polite');

          var icon = document.createElement('span');
          icon.className = 'gift-campaign-badge__icon';
          icon.setAttribute('aria-hidden', 'true');
          icon.textContent = '🎁';

          var text = document.createElement('span');
          text.className = 'gift-campaign-badge__text';

          var close = document.createElement('button');
          close.type = 'button';
          close.className = 'gift-campaign-badge__close';
          close.setAttribute('aria-label', 'Fechar aviso de brinde');
          close.textContent = '×';
          close.addEventListener('click', this.dismiss.bind(this));

          pill.appendChild(icon);
          pill.appendChild(text);
          pill.appendChild(close);
          document.body.appendChild(pill);

          this._pill = pill;
          this._textEl = text;
        }

        onCartUpdate(event) {
          if (event && event.detail && Array.isArray(event.detail.items)) {
            this.render(event.detail);
          } else {
            this.refresh();
          }
        }

        refresh() {
          var self = this;
          fetch('/cart.js')
            .then(function (response) { return response.json(); })
            .then(function (cart) { self.render(cart); })
            .catch(function () {});
        }

        render(cart) {
          var cfg = window.giftSystemConfig;
          var badge = cfg && cfg.campaignBadge;

          if (
            !cfg ||
            cfg.enabled !== true ||
            !badge ||
            badge.enabled !== true ||
            !cart ||
            !Array.isArray(cart.items)
          ) {
            this.hide();
            return;
          }

          var view = resolveBadgeView(cfg, cart);

          if (!view.visible) {
            this.hide();
            return;
          }

          var signature = campaignSignature(cfg.levels);
          this._currentSignature = signature;

          if (this.dismissedSignature() === signature) {
            this.hide();
            return;
          }

          this._textEl.textContent = resolveBadgeMessage(view, cfg, badge);
          this.show();
        }

        dismiss() {
          try {
            localStorage.setItem(DISMISS_KEY, this._currentSignature || '1');
          } catch (error) {
            // ignora storage indisponível
          }
          this.hide();
        }

        dismissedSignature() {
          try {
            return localStorage.getItem(DISMISS_KEY);
          } catch (error) {
            return null;
          }
        }

        show() {
          if (this._pill) {
            this._pill.setAttribute('data-visible', 'true');
          }
        }

        hide() {
          if (this._pill) {
            this._pill.setAttribute('data-visible', 'false');
          }
        }
      }
      : null
  );

  if (
    typeof customElements !== 'undefined' &&
    GiftCampaignBadge &&
    !customElements.get('gift-campaign-badge')
  ) {
    customElements.define('gift-campaign-badge', GiftCampaignBadge);
  }
})();
